/**
 * Audio Sanctum Design System
 * ================================
 * Single source of truth for all design tokens.
 * Import from here instead of hardcoding values.
 *
 * Usage:
 *   import { colors, fonts, radius, shadows, spacing, transitions } from "@/styles/design-system";
 *
 *   const MyComponent = styled.div`
 *     background: ${colors.surface.card};
 *     font-family: ${fonts.heading};
 *     border-radius: ${radius.md};
 *   `;
 */

/* ═══════════════════════════════════════════
   COLORS
   ═══════════════════════════════════════════ */

export const colors = {
  /* ── Brand Gold ── */
  gold: {
    primary: "#ba905c",
    light: "#ffd29b",
    lighter: "#ffe0b1",
    accent: "#ffdca8",
    text: "#f3d0a4",
    heading: "#ffe2b9",
    gradient: "linear-gradient(93deg, #ba905c 0.82%, #ffd29b 33.73%, #ffe0b1 47.65%, #ffd29b 61.53%, #ba905c 99.48%)",
  },

  /* ── Brown / Base ── */
  brown: {
    darkest: "#3c2d23",
    dark: "#3a2616",
    primary: "#59412e",
    medium: "#513a28",
    page: "#715137",
  },

  /* ── Text ── */
  text: {
    primary: "#fbf4ec",
    heading: "#ffffff",
    body: "#e0d2c1",
    secondary: "#e0d2c1",
    muted: "rgba(251, 244, 236, 0.5)",
    mutedLight: "rgba(251, 244, 236, 0.45)",
    subtle: "#ba9c7e",
    link: "#ebc28e",
    linkHover: "#f7ede6",
    artist: "#efd0b8",
    // Peach accent — small uppercase labels inside modals (e.g.
    // "QUESTION 1 OF 6" step counter in QuestionFlowDialog).
    accentPeach: "#ffdab9",
  },

  /* ── Light surface (menus, footer, dropdowns) ── */
  white: "#fffbf7",

  /* ── Footer (Light Theme) ── */
  footer: {
    bg: "#fffbf7",
    surface: "#fffbf7",
    text: "#3a2616",
    textMuted: "rgba(58, 38, 22, 0.45)",
    link: "#5a3d2a",
    linkHover: "#3a2616",
    divider: "rgba(58, 38, 22, 0.1)",
    inputBg: "rgba(255, 255, 255, 0.5)",
    inputBorder: "rgba(58, 38, 22, 0.12)",
  },

  /* ── Menu (Light Theme) ── */
  menu: {
    bg: "#fffbf7",
    text: "#5C3D28",
    textHover: "#3a2616",
    hoverBg: "rgba(89, 65, 46, 0.08)",
    divider: "rgba(89, 65, 46, 0.12)",
    avatar: "#59412E",
  },

  /* ── Surfaces ── */
  surface: {
    page: "#715137",
    modal: "#59412e",
    card: "rgba(81, 58, 40, 0.6)",
    cardHover: "rgba(81, 58, 40, 0.8)",
    input: "#513a28",
    sidebar: "rgba(75, 55, 39, 0.25)",
    panel: "rgba(75, 55, 39, 0.35)",
    glass: "rgba(255, 255, 255, 0.06)",
    glassHover: "rgba(255, 255, 255, 0.12)",
    overlay: "rgba(0, 0, 0, 0.7)",
    planCard: "rgba(113, 81, 55, 0.2)",
    planCardHover: "linear-gradient(135deg, rgba(186, 144, 92, 0.2) 0%, rgba(255, 210, 155, 0.2) 100%)",
  },

  /* ── Borders ── */
  border: {
    default: "rgba(235, 194, 142, 0.3)",
    light: "rgba(235, 194, 142, 0.15)",
    veryLight: "rgba(235, 194, 142, 0.08)",
    medium: "rgba(186, 144, 92, 0.2)",
    input: "rgba(235, 194, 142, 0.2)",
    inputFocus: "rgba(235, 194, 142, 0.5)",
    white: "rgba(255, 255, 255, 0.06)",
    whiteHover: "rgba(255, 255, 255, 0.12)",
    headerIcon: "rgba(255, 255, 255, 0.5)",
  },

  /* ── Links ── */
  link: {
    blue: "#9cd3fd",
    gold: "#ffe2b9",
  },

  /* ── Accent / Status ── */
  accent: {
    blue: "#9cd3fd",
    blueLight: "#b0dfff",
    blueDark: "#8bc5f0",
    blueDeep: "#193e5b",
    blueIcon: "#60a3d7",
    green: "#34d399",
    greenDark: "#2bc47a",
    greenBg: "rgba(52, 211, 153, 0.15)",
    red: "#e47a6a",
    redBright: "#FF5935",
  },

  /* ── Tier Pills ── */
  tier: {
    free: {
      /** The banners' green, and the same gradient they carry — lighter at the
          start, the true colour in the middle, deeper at the end. The pill and
          the "it went well" banners now say green the same way, instead of the
          pill saying a flat translucent teal and the banners a forest green.
          Kept literal rather than calling `bannerGradient()`: this module is
          the token source and should not depend on a component helper. */
      bg: "linear-gradient(100deg, rgb(47, 151, 53) 0%, rgb(46, 125, 50) 45%, rgb(54, 128, 58) 100%)",
      text: "#f0e8e2",
    },
    /** Subscription pill — blue, white type. It was gold until 2026-07-30, and
        gold is the button language here: a pill wearing the button gradient
        reads as something to press rather than as a state. Blue also puts
        clear water between it and the green Free pill it sits beside.
        (Replaces the legacy split between `tier.essential` (pink/purple) and
        `tier.complete` (gold) from when there were two paid tiers.) */
    subscription: {
      bg: "linear-gradient(100deg, rgb(60 155 180) 0%, rgb(68, 123, 180) 55%, rgb(51, 111, 138) 100%)",
      text: "#ffffff",
    },
    /** Premium track tier — used on the catalog player + cards to mark
        tracks as subscription-only. Different concept from the `tier`
        plan key above; keep both in sync visually. */
    premium: {
      bg: "linear-gradient(100deg, rgb(60 155 180) 0%, rgb(68, 123, 180) 55%, rgb(51, 111, 138) 100%)",
      text: "#ffffff",
    },
  },

  /* ── Prices ── */
  price: {
    primary: "#9cd3fd",
    /** The "$0" on the Free plan. Brighter than `accent.green` (the tick green)
        on purpose: a price is the largest type in its box and the tick green
        goes muddy at that weight against the card. */
    free: "#3de599",
  },
} as const;

/* ═══════════════════════════════════════════
   TYPOGRAPHY
   ═══════════════════════════════════════════ */

export const fonts = {
  heading: '"Cormorant Garamond", serif',
  body: '"Raleway", sans-serif',
  number: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
} as const;

/**
 * Font Size Scale
 * ─────────────────────────────────────────
 * 7 sizes only. Pick the closest one — don't invent new sizes.
 *
 * ROLE GUIDE:
 *   xs   — Badges, pills, tier labels, table footnotes
 *   sm   — Form labels, captions, action links, sidebar items, metadata
 *   body — Standard text everywhere: inputs, placeholders, buttons, descriptions,
 *          paragraphs, card text, sidebar nav, dropdown items, filter items
 *   lg   — Sub-headings, card titles, section headers, nav items
 *   xl   — Track titles, album titles, modal titles
 *   2xl  — Page headings (e.g. "Account", "Browse Music")
 *   3xl  — Hero text, large display (rare)
 *
 * Numbers (durations, counts, prices, pagination) always use fonts.number.
 */
/**
 * Font Size Scale — 7 sizes, no in-betweens.
 * ─────────────────────────────────────────────────────
 * Everything on the site should use one of these tokens.
 * If you're tempted to add a new size, pick the closest one instead.
 *
 *   xs   → Badges, pills, tier labels
 *   sm   → Form labels, captions, action links, footnotes
 *   body → THE default. Inputs, buttons, nav, descriptions, addresses, paragraphs
 *   lg   → Sub-headings, card titles, section titles (often Cormorant)
 *   xl   → Track/album titles, modal headers
 *   2xl  → Page headings ("Account", "Browse Music")
 *   3xl  → Hero display text (rare)
 *
 * Numbers (durations, prices, counts) → always use fonts.number with the appropriate size.
 */
export const fontSizes = {
  xs: "0.6875rem",   // 11px — badges, pills
  sm: "0.8125rem",   // 13px — form labels, captions, action links
  body: "0.9375rem", // 15px — the standard. inputs, buttons, nav, descriptions, paragraphs
  lg: "1.125rem",    // 18px — sub-headings, card titles, section titles
  xl: "1.25rem",     // 20px — track/album titles, modal headers
  "2xl": "1.5rem",   // 24px — page headings
  "3xl": "2rem",     // 32px — hero / display (rare)
} as const;

export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/* ═══════════════════════════════════════════
   SPACING
   ═══════════════════════════════════════════ */

export const spacing = {
  "2xs": "2px",
  xs: "4px",
  sm: "6px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  "2xl": "20px",
  "3xl": "24px",
  "4xl": "32px",
  "5xl": "40px",
  "6xl": "48px",
} as const;

/* ═══════════════════════════════════════════
   TYPOGRAPHY SPACING
   ═══════════════════════════════════════════ */

/**
 * Standard gaps between headings and their following content.
 * Use these instead of ad-hoc margin values.
 *
 *   headingToContent — 16px — After any heading before body text / description / lists.
 *                      Derived from the Licenses page (ContentTitle → PageDescription).
 *                      Applies everywhere: modals, page sections, cards.
 */
export const headingSpacing = {
  headingToContent: spacing.xl, // 16px
} as const;

/* ═══════════════════════════════════════════
   BORDER RADIUS
   ═══════════════════════════════════════════ */

export const radius = {
  xs: "4px",        // scrollbar, tiny elements
  sm: "6px",        // tabs, small buttons, checkboxes
  md: "8px",        // inputs, buttons, search
  lg: "10px",       // pills, newsletter inputs
  xl: "12px",       // cards, panels, content areas
  "2xl": "16px",    // plan cards, modal headers
  "3xl": "20px",    // cart pill, pill buttons
  "4xl": "24px",    // modal container
  full: "50%",      // circles
} as const;

/* ═══════════════════════════════════════════
   SHADOWS
   ═══════════════════════════════════════════ */

export const shadows = {
  sm: "0 2px 4px rgba(0, 0, 0, 0.5)",
  md: "0 4px 20px rgba(0, 0, 0, 0.15)",
  lg: "0 8px 32px rgba(186, 144, 92, 0.4)",
  xl: "0 20px 60px rgba(0, 0, 0, 0.6)",
  dropdown: "0 12px 32px rgba(0, 0, 0, 0.2)",
  menu: "drop-shadow(0px 16px 48px rgba(0,0,0,0.25))",
  glow: {
    gold: "0 0 20px rgba(186, 144, 92, 0.3)",
    goldStrong: "0 0 40px rgba(186, 144, 92, 0.6)",
    green: "0 0 14px rgba(52, 211, 153, 0.4)",
    blue: "0 0 4px rgba(156, 211, 253, 0.3)",
    checkbox: "0 0 32px rgba(235, 194, 142, 0.6)",
  },
  button: {
    primary: "0 0 32px rgba(186, 144, 92, 0.4)",
    primaryHover: "0 0 40px rgba(186, 144, 92, 0.6)",
    pill: "0 4px 20px rgba(0, 0, 0, 0.15)",
    pillHover: "0 6px 24px rgba(0, 0, 0, 0.2)",
  },
} as const;

/* ═══════════════════════════════════════════
   TRANSITIONS
   ═══════════════════════════════════════════ */

export const transitions = {
  fast: "0.15s ease",       // quick state changes (qty buttons)
  normal: "0.2s ease",      // standard UI feedback
  slow: "0.3s ease",        // modals, cards, larger elements
  slower: "0.35s ease",     // player bar, complex animations
  spring: "0.3s cubic-bezier(0.4, 0, 0.2, 1)", // Material-style
} as const;

/* ═══════════════════════════════════════════
   BREAKPOINTS
   ═══════════════════════════════════════════ */

export const breakpoints = {
  xs: "480px",
  sm: "640px",
  md: "768px",
  lg: "860px",
  xl: "1024px",
  "2xl": "1200px",
} as const;

/* ═══════════════════════════════════════════
   Z-INDEX LAYERS
   ═══════════════════════════════════════════ */

export const zIndex = {
  sidebar: 100,
  header: 200,
  player: 300,
  dropdown: 400,
  modal: 1300,
  termsModal: 2000,
  tooltip: 3000,
} as const;

/* ═══════════════════════════════════════════
   COMPONENT TOKENS (common patterns)
   ═══════════════════════════════════════════ */

export const components = {
  /* ── Backdrop blur surfaces ── */
  glass: {
    background: colors.surface.glass,
    border: `1px solid ${colors.border.white}`,
    backdropFilter: "blur(20px)",
  },

  /* ── Standard card ── */
  card: {
    background: colors.surface.card,
    border: `1px solid ${colors.border.light}`,
    borderRadius: radius.xl,
    padding: spacing["2xl"],
  },

  /* ── Elevated card (with top accent) ── */
  cardElevated: {
    background: colors.surface.card,
    border: `1px solid ${colors.border.light}`,
    borderTop: `3px solid rgba(235, 194, 142, 0.4)`,
    borderRadius: radius.xl,
  },

  /**
   * ── Modal — frosted glass with shadow overlay ──
   *
   * IMPORTANT PATTERNS:
   *
   * 1. Blur is ALWAYS static CSS — never toggle via props (causes flash).
   * 2. Unmount when closed — use `useModalAnimation` hook for exit animation.
   * 3. Close button uses ✕ character (&#x2715;) at 1.1rem — NOT an SVG icon.
   * 4. Multi-step modals: animate height via forced-reflow pattern (see DownloadModal).
   *    Do NOT use React useState for viewport height — it batches and snaps.
   *    Instead: read current height → freeze with transition:none → force reflow →
   *    set transition + new height. See memory/feedback_modal_blur_fix.md for full code.
   * 5. Freeze step on close: when closing resets activeStep, use a ref to keep the
   *    current step during the slideDown animation to prevent sliding back.
   */
  modal: {
    // Static gradient — three radial "lobes" + vertical linear, captured
    // as a snapshot of the previous frosted-glass look so modals render
    // consistently regardless of what's behind them. Source of truth lives
    // in `src/components/Dialog/components.tsx` (`.MuiDialog-paper`); the
    // multi-line CSS string here matches that exactly. See
    // `docu/modal-architecture.md` for usage + the legacy frosted-glass
    // recipe (kept for reference if we ever want to revert).
    background: `radial-gradient(ellipse 45% 40% at 42% -3%, rgba(176, 144, 105, 0.9) 0%, rgba(160, 128, 90, 0.5) 35%, transparent 75%),
      radial-gradient(ellipse 55% 50% at 58% 3%, rgba(170, 140, 100, 0.75) 0%, rgba(140, 110, 78, 0.35) 45%, transparent 85%),
      radial-gradient(ellipse 70% 35% at 50% 8%, rgba(150, 118, 82, 0.4) 0%, transparent 70%),
      linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%)`,
    // Frosted-glass blur is no longer the active recipe but kept here
    // (and in `docu/modal-architecture.md`) as a reference for future
    // overlay/tray surfaces that need the live-blur look.
    legacyBackdropFilter: "blur(64px)",
    legacyBackground: "rgba(220, 180, 150, 0.15)",
    border: "none",
    borderRadius: "24px",
    boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.7), 0 20px 60px rgba(0, 0, 0, 0.6)",
    overlay: "transparent",
    closeBtn: {
      size: "36px",
      background: "rgba(235, 194, 142, 0.2)",
      border: "1px solid rgba(235, 194, 142, 0.3)",
      color: "#fff",
      hoverBg: "rgba(235, 194, 142, 0.35)",
      hoverBorderColor: "rgba(235, 194, 142, 0.5)",
    },
    // Solid deep-brown disk variant — sits OUTSIDE the modal card
    // (e.g. floating top-right corner button on QuestionFlowDialog),
    // where the translucent gold pill would clash with the page behind.
    closeBtnSolid: {
      size: "38px",
      background: "#5D4633",
      hoverBg: "#6c5240",
      border: "1px solid rgba(255, 255, 255, 0.25)",
      color: "#ffffff",
    },
  },

  /* ── Input field (dark) ── */
  input: {
    background: colors.surface.input,
    border: `1px solid ${colors.border.input}`,
    borderRadius: radius.md,
    padding: "14px 16px",
    color: colors.text.body,
    fontFamily: fonts.body,
    fontSize: fontSizes.body,
    placeholderColor: "rgba(185, 160, 137, 0.7)",
    focusBorder: colors.border.inputFocus,
  },

  /* ── Input field (glassy golden) ── */
  inputGlassy: {
    background: "rgba(235, 194, 142, 0.06)",
    border: "1px solid rgba(235, 194, 142, 0.25)",
    borderRadius: radius.md,
    padding: "14px 16px",
    color: "#ffffff",
    fontFamily: fonts.body,
    fontSize: fontSizes.body,
    placeholderColor: "rgba(255, 255, 255, 0.6)",
    focusBg: "rgba(235, 194, 142, 0.1)",
    focusBorder: "rgba(235, 194, 142, 0.5)",
    focusGlow: "0 0 12px rgba(186, 144, 92, 0.15)",
    hoverBorder: "rgba(235, 194, 142, 0.35)",
    backdropFilter: "blur(12px)",
  },

  /* ── Primary button ── */
  buttonPrimary: {
    background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
    color: colors.brown.darkest,
    borderRadius: radius.md,
    padding: "14px 28px",
    fontFamily: fonts.body,
    fontSize: fontSizes.body,
    fontWeight: fontWeights.bold,
    boxShadow: shadows.button.primary,
    hoverTransform: "translateY(-2px)",
    hoverShadow: shadows.button.primaryHover,
  },

  /* ── Secondary button ── */
  buttonSecondary: {
    background: "transparent",
    border: `1px solid rgba(235, 194, 142, 0.4)`,
    color: colors.text.link,
    borderRadius: radius.sm,
    padding: "12px 20px",
    fontFamily: fonts.body,
    fontSize: fontSizes.body,
    fontWeight: fontWeights.semibold,
    hoverBg: "rgba(235, 194, 142, 0.1)",
  },

  /* ── Ghost button ── */
  buttonGhost: {
    background: "transparent",
    border: "none",
    color: colors.text.body,
    padding: "14px",
    fontFamily: fonts.body,
    hoverColor: colors.text.link,
    hoverBg: "rgba(235, 194, 142, 0.08)",
  },

  /* ── Pill tab ── */
  pillTab: {
    height: "36px",
    padding: "0 20px",
    borderRadius: radius.sm,
    fontFamily: fonts.body,
    fontSize: fontSizes.body,
    fontWeight: fontWeights.regular,
    activeColor: colors.text.primary,
    activeBg: "rgba(255, 220, 168, 0.15)",
    inactiveColor: colors.text.mutedLight,
    inactiveBg: "transparent",
    hoverColor: colors.text.primary,
    hoverBg: colors.surface.glass,
  },

  /* ── Pill tab container ── */
  pillTabContainer: {
    background: colors.surface.glass,
    border: `1px solid ${colors.border.medium}`,
    borderRadius: radius.md,
    padding: "3px",
    gap: "4px",
  },

  /* ── Checkbox ── */
  checkbox: {
    size: "24px",
    borderRadius: radius.xs,
    borderDefault: `2px solid rgba(235, 194, 142, 0.4)`,
    borderChecked: `2px solid #9cd3fd`,
    bgChecked: "#9cd3fd",
    glowChecked: "0 0 20px rgba(156, 211, 253, 0.4)",
  },

  /* ── Page title — "Account", "Browse Music", etc. ── */
  pageTitle: {
    fontFamily: fonts.heading,
    fontSize: fontSizes["3xl"],
    fontWeight: fontWeights.bold,
    color: colors.text.heading,
  },

  /* ── Section label — "USER", "PASSWORD", etc. ── */
  sectionLabel: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.medium,
    color: colors.text.primary,
    textTransform: "uppercase" as const,
    letterSpacing: "2px",
    lineColor: "rgba(235, 194, 142, 0.2)",
  },

  /* ── Accordion ── */
  accordion: {
    background: colors.surface.card,
    backgroundExpanded: colors.surface.cardHover,
    border: `1px solid ${colors.border.light}`,
    borderHover: `1px solid ${colors.border.default}`,
    borderExpanded: `1px solid rgba(235, 194, 142, 0.35)`,
    borderRadius: radius.xl,
    padding: "20px",
    title: {
      fontFamily: fonts.heading,
      fontSize: "1.375rem", // 22px — between xl and 2xl, visually matches track titles in context
      fontWeight: fontWeights.semibold,
      color: colors.text.primary,
    },
    description: {
      fontFamily: fonts.body,
      fontSize: fontSizes.body,
      fontWeight: fontWeights.regular,
      color: "rgba(251, 244, 236, 0.7)",
    },
    icon: {
      color: "rgba(235, 194, 142, 0.5)",
      colorExpanded: colors.text.link,
    },
  },

  /* ── Scrollbar ── */
  scrollbar: {
    width: "8px",
    track: "rgba(60, 45, 35, 0.5)",
    thumb: "rgba(235, 194, 142, 0.5)",
    borderRadius: radius.xs,
  },
} as const;
